import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';

import { ReportComponentType } from '@/types';

import { OPEN_MEMO_REPORT_COMPONENT_PICKER_EVENT } from '../ReportComponentEmbed/events';
import CommandsList, { type MemoSlashCommandItem } from './CommandsList';
import { MemoMediaType, OPEN_MEMO_MEDIA_INSERT_MODAL_EVENT } from './mediaInsertEvents';

const openReportComponentPicker = ({ editor, range, props }: any) => {
    editor.chain().focus().deleteRange(range).run();

    window.dispatchEvent(
        new CustomEvent(OPEN_MEMO_REPORT_COMPONENT_PICKER_EVENT, {
            detail: { editor, componentType: props?.reportComponentType },
        }),
    );
};

const openMediaInsertModal =
    (mediaType: MemoMediaType) =>
    ({ editor, range }: any) => {
        editor.chain().focus().deleteRange(range).run();

        window.dispatchEvent(
            new CustomEvent(OPEN_MEMO_MEDIA_INSERT_MODAL_EVENT, {
                detail: { editor, mediaType },
            }),
        );
    };

const MEMO_RICH_COMPONENT_SECTIONS = new Set(['Tables', 'Media', 'Report Components', 'Embeds']);

export const createMemoSlashCommands = (): MemoSlashCommandItem[] => [
    {
        title: 'Table',
        description: 'Resizable rows and columns',
        section: 'Tables',
        iconKey: 'table',
        keywords: ['grid', 'rows', 'columns', 'cells'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    },
    {
        title: 'Paragraph',
        description: 'Text',
        section: 'Typography',
        iconKey: 'paragraph',
        shortcut: '⌘⌥0',
        keywords: ['text', 'body'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).setNode('paragraph').run(),
    },
    {
        title: 'Heading 1',
        description: 'Large section heading',
        section: 'Typography',
        iconKey: 'heading1',
        shortcut: '⌘⌥1',
        keywords: ['h1', 'title'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
    },
    {
        title: 'Heading 2',
        description: 'Medium section heading',
        section: 'Typography',
        iconKey: 'heading2',
        shortcut: '⌘⌥2',
        keywords: ['h2', 'subtitle'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
    },
    {
        title: 'Heading 3',
        description: 'Small section heading',
        section: 'Typography',
        iconKey: 'heading3',
        shortcut: '⌘⌥3',
        keywords: ['h3'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
    },
    {
        title: 'Heading 4',
        description: 'Compact heading',
        section: 'Typography',
        iconKey: 'heading4',
        shortcut: '⌘⌥4',
        keywords: ['h4'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 4 }).run(),
    },
    {
        title: 'Heading 5',
        description: 'Compact heading',
        section: 'Typography',
        iconKey: 'heading5',
        shortcut: '⌘⌥5',
        keywords: ['h5'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 5 }).run(),
    },
    {
        title: 'Heading 6',
        description: 'Compact heading',
        section: 'Typography',
        iconKey: 'heading6',
        shortcut: '⌘⌥6',
        keywords: ['h6'],
        command: ({ editor, range }: any) =>
            editor.chain().focus().deleteRange(range).setNode('heading', { level: 6 }).run(),
    },
    {
        title: 'Bulleted List',
        description: 'List',
        section: 'Lists',
        iconKey: 'bulletedList',
        shortcut: '⌘⇧8',
        keywords: ['bullet', 'unordered'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
    },
    {
        title: 'Numbered List',
        description: 'List',
        section: 'Lists',
        iconKey: 'numberedList',
        shortcut: '⌘⇧9',
        keywords: ['ordered'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
    },
    {
        title: 'Checklist',
        description: 'Task list',
        section: 'Lists',
        iconKey: 'checklist',
        shortcut: '⌘⇧7',
        keywords: ['task', 'todo'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).toggleTaskList().run(),
    },
    {
        title: 'Callout',
        description: 'Highlighted block',
        section: 'Blocks',
        iconKey: 'callout',
        keywords: ['note', 'info', 'warning'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).setCallout('info').run(),
    },
    {
        title: 'Horizontal Rule',
        description: 'Divider',
        section: 'Blocks',
        iconKey: 'divider',
        keywords: ['line', 'separator'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
    {
        title: 'Blockquote',
        description: 'Quote',
        section: 'Blocks',
        iconKey: 'blockquote',
        shortcut: '⌘⇧.',
        keywords: ['quote'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    },
    {
        title: 'Code Block',
        description: 'Code',
        section: 'Blocks',
        iconKey: 'codeBlock',
        shortcut: '⌘⇧\\',
        keywords: ['code', 'pre'],
        command: ({ editor, range }: any) => editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    // Image and Video insertion remain implemented, but are intentionally unavailable
    // while the Memo media workflow is held back from user-facing creation surfaces.
    // {
    //     title: 'Image',
    //     description: 'Media',
    //     section: 'Media',
    //     iconKey: 'image',
    //     keywords: ['photo', 'media'],
    //     command: openMediaInsertModal('image'),
    // },
    // {
    //     title: 'Video',
    //     description: 'Media',
    //     section: 'Media',
    //     iconKey: 'video',
    //     keywords: ['youtube', 'video', 'media'],
    //     command: openMediaInsertModal('video'),
    // },
    {
        title: 'Report Component',
        description: 'Dashboard component',
        section: 'Report Components',
        iconKey: 'reportComponent',
        keywords: ['chart', 'dashboard', 'visualization', 'vantage'],
        command: openReportComponentPicker,
    },
    {
        title: 'Vantage Query',
        description: 'Report component',
        section: 'Report Components',
        reportComponentType: ReportComponentType.COUNTERCYCLICAL_VANTAGE,
        keywords: ['sql', 'query', 'data'],
        command: openReportComponentPicker,
    },
    {
        title: 'Embed',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.EMBED,
        keywords: ['iframe', 'url', 'app'],
        command: openReportComponentPicker,
    },
    {
        title: 'YouTube',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.YOUTUBE,
        keywords: ['video'],
        command: openMediaInsertModal('video'),
    },
    {
        title: 'Loom',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.LOOM,
        keywords: ['video'],
        command: openReportComponentPicker,
    },
    {
        title: 'Vimeo',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.VIMEO,
        keywords: ['video'],
        command: openReportComponentPicker,
    },
    {
        title: 'Google Calendar',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GOOGLE_CALENDAR,
        keywords: ['calendar'],
        command: openReportComponentPicker,
    },
    {
        title: 'Outlook Calendar',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR,
        keywords: ['calendar', 'microsoft'],
        command: openReportComponentPicker,
    },
    {
        title: 'Google Drive',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GOOGLE_DRIVE,
        keywords: ['file', 'document'],
        command: openReportComponentPicker,
    },
    {
        title: 'Google Docs',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GOOGLE_DOCS,
        keywords: ['document'],
        command: openReportComponentPicker,
    },
    {
        title: 'Google Sheets',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GOOGLE_SHEETS,
        keywords: ['spreadsheet'],
        command: openReportComponentPicker,
    },
    {
        title: 'Microsoft OneDrive',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.MICROSOFT_ONEDRIVE,
        keywords: ['file', 'document'],
        command: openReportComponentPicker,
    },
    {
        title: 'Dropbox',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.DROPBOX,
        keywords: ['file'],
        command: openReportComponentPicker,
    },
    {
        title: 'Box',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.BOX,
        keywords: ['file'],
        command: openReportComponentPicker,
    },
    {
        title: 'Notion',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.NOTION,
        keywords: ['document'],
        command: openReportComponentPicker,
    },
    {
        title: 'GitHub',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GITHUB,
        keywords: ['code'],
        command: openReportComponentPicker,
    },
    {
        title: 'Hex',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.HEX,
        keywords: ['notebook', 'data'],
        command: openReportComponentPicker,
    },
    {
        title: 'Confluence',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.CONFLUENCE,
        keywords: ['document'],
        command: openReportComponentPicker,
    },
    {
        title: 'Miro',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.MIRO,
        keywords: ['whiteboard'],
        command: openReportComponentPicker,
    },
    {
        title: 'Lucid',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.LUCID,
        keywords: ['diagram'],
        command: openReportComponentPicker,
    },
    {
        title: 'Graphy',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.GRAPHY,
        keywords: ['chart'],
        command: openReportComponentPicker,
    },
    {
        title: 'Microsoft Power BI',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.MICROSOFT_POWER_BI,
        keywords: ['dashboard', 'bi'],
        command: openReportComponentPicker,
    },
    {
        title: 'Looker',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.LOOKER,
        keywords: ['dashboard', 'bi'],
        command: openReportComponentPicker,
    },
    {
        title: 'Tableau',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.TABLEAU,
        keywords: ['dashboard', 'bi'],
        command: openReportComponentPicker,
    },
    {
        title: 'Zoho Analytics',
        description: 'Embed',
        section: 'Embeds',
        reportComponentType: ReportComponentType.ZOHO_ANALYTICS,
        keywords: ['dashboard', 'bi'],
        command: openReportComponentPicker,
    },
];

export const createMemoRichComponentCommands = () =>
    createMemoSlashCommands().filter((command) => MEMO_RICH_COMPONENT_SECTIONS.has(command.section));

const commandsSuggestion = {
    items: () => createMemoSlashCommands() as any,

    render: () => {
        let component: any;
        let popup: any;

        return {
            onStart: (props: any) => {
                component = new ReactRenderer(CommandsList, {
                    props,
                    editor: props.editor,
                });

                popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    maxWidth: 'none',
                    offset: [0, 8],
                });
            },
            onUpdate(props: any) {
                component.updateProps(props);

                popup[0].setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },
            onKeyDown(props: any) {
                if (props.event.key === 'Escape') {
                    popup[0].hide();

                    return true;
                }

                return component.ref?.onKeyDown(props);
            },
            onExit() {
                popup[0].destroy();
                component.destroy();
            },
        };
    },
};

export default commandsSuggestion;
